/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  inject,
  Inject,
  Injectable,
  PLATFORM_ID,
  signal,
  WritableSignal,
} from '@angular/core';
import { Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { EncryptionService } from './encryption.service';
import { assets } from '../core/constants/assets.const';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { AudioService } from './audio.service';
import { I18nService } from './i18n.service';
import {
  parseNotificationPayload,
  resolveCustomSoundFromPushData,
  resolveNotificationAudioKey,
} from '../core/helpers/notification-sound.helper';

const MAX_HANDLED_SOUND_IDS = 50;

@Injectable({
  providedIn: 'root',
})
export class MessagingService {
  private isBrowser: boolean;

  assetsConst = assets;

  private receivedMessageIds = new Set<string>();
  private handledSoundMessageIds = new Set<string>();

  // ✅ Signal for backward-compat reads
  private _message: WritableSignal<any | null> = signal(null);
  readonly currentMessage = this._message.asReadonly();

  // ✅ Subject stream — emits each message as a discrete event (no infinite-loop risk)
  private _message$ = new Subject<any>();
  readonly message$ = this._message$.asObservable();

  private audioService = inject(AudioService);

  constructor(
    private encryption: EncryptionService,
    private translate: I18nService,
    @Inject(PLATFORM_ID) platformId: object,
    private router: Router
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /**
   * Firebase lazy initialization
   */
  private async getFirebaseMessaging() {
    const { initializeApp, getApps } = await import('firebase/app');
    const { getMessaging } = await import('firebase/messaging');

    const app = getApps().length
      ? getApps()[0]
      : initializeApp(environment.firebaseConfig);

    return getMessaging(app);
  }

  /**
   * Request notification permission
   */
  async requestPermission(): Promise<void> {
    if (!this.isBrowser) return;

    const { getToken } = await import('firebase/messaging');
    const messaging = await this.getFirebaseMessaging();

    navigator.serviceWorker.ready
      .then(registration =>
        getToken(messaging, {
          vapidKey: environment.firebaseConfig.vapidKey,
          serviceWorkerRegistration: registration,
        })
      )
      .then(currentToken => {
        if (currentToken) {
          this.encryption.encryptLocalStorage(currentToken, 'fcmToken');
        } else {
          alert(this.translate.translate('common.fcmNotification'));
        }
      })
      .catch(err => {
        console.log('Token retrieval error:', err);
      });
  }

  /**
   * Listen for foreground notifications
   */
  async listenForFrontMessages(): Promise<void> {
    if (!this.isBrowser) return;

    const { onMessage } = await import('firebase/messaging');
    const messaging = await this.getFirebaseMessaging();

    if (localStorage[environment.storageKey]) {
      onMessage(messaging, (payload: any) => {
        const data = (payload.data ?? {}) as Record<string, unknown>;
        const payloadData = parseNotificationPayload(data['payload']);
        console.log('FCM foreground push (raw payload)', payload);
        console.log('FCM foreground push data key/values', data);
        console.log('FCM foreground nested payload', payloadData);
        console.log('FCM foreground custom_sound (nested payload only)', {
          custom_sound: payloadData['custom_sound'],
          sound_type: payloadData['sound_type'],
        });

        const messageId = this.readMessageId(
          payload as Record<string, unknown>,
          data
        );

        if (messageId && this.receivedMessageIds.has(messageId)) {
          console.log('Duplicate notification ignored:', messageId);
          return;
        }

        if (messageId) {
          this.receivedMessageIds.add(messageId);
        }

        const shouldPlaySound = resolveCustomSoundFromPushData(data);
        console.log('FCM foreground shouldPlaySound', shouldPlaySound);

        // Do not play/stop here. The SW raw `push` handler posts
        // playNotificationSound / refreshNotification so false→true cannot
        // race with a second onMessage applyPushSound using a different id.

        this.displayNotification(String(data['title'] || 'New Notification'), {
          body: String(data['body'] || 'Click to view'),
          icon: this.assetsConst.logo,
          data: payload.data,
          silent: !shouldPlaySound,
        });

        this.sendMessage({ action: 'refresh' });
      });
    }
  }

  /**
   * Display browser notification
   */
  private displayNotification(
    title: string,
    options: NotificationOptions
  ): void {
    const currentLang = this.translate.getCurrentLang() || 'en';
    const isRtl = this.isRtlLanguage(currentLang);

    options = {
      ...options,
      dir: isRtl ? 'rtl' : 'ltr',
    };

    navigator.serviceWorker.ready.then(registration => {
      if (registration) {
        registration.showNotification(title, options);
      } else {
        console.error('Service worker registration not found.');
      }
    });
  }

  private isRtlLanguage(lang: string): boolean {
    const rtlLanguages = ['ar'];
    return rtlLanguages.includes(lang);
  }

  /**
   * Apply this push's nested custom_sound only (true → play, false → mute).
   * Each push is independent: a muted push must not block the next unmuted one.
   * Mute always stops audio (even on duplicate ids). Play is deduped by messageId.
   */
  applyPushSound(
    shouldPlaySound: boolean,
    type: unknown,
    messageId?: unknown,
    soundType?: unknown
  ): void {
    if (!shouldPlaySound) {
      this.audioService.stopAll();
      this.markSoundHandled(messageId);
      return;
    }

    if (this.markSoundHandled(messageId)) {
      return;
    }

    const audioKey = resolveNotificationAudioKey(
      type,
      typeof soundType === 'string' ? soundType : undefined,
      this.router.url.startsWith('/sp-bids/tracking/')
    );
    this.audioService.play(audioKey);
  }

  private readMessageId(
    payload: Record<string, unknown>,
    data: Record<string, unknown>
  ): string {
    const fromPayload = payload['messageId'];
    const fromFcm = payload['fcmMessageId'] ?? data['fcmMessageId'];
    if (typeof fromPayload === 'string' && fromPayload) {
      return fromPayload;
    }
    if (typeof fromFcm === 'string' && fromFcm) {
      return fromFcm;
    }
    return '';
  }

  private markSoundHandled(messageId: unknown): boolean {
    if (typeof messageId !== 'string' || !messageId) {
      return false;
    }

    if (this.handledSoundMessageIds.has(messageId)) {
      return true;
    }

    this.handledSoundMessageIds.add(messageId);
    if (this.handledSoundMessageIds.size > MAX_HANDLED_SOUND_IDS) {
      const oldest = this.handledSoundMessageIds.values().next().value;
      if (oldest) {
        this.handledSoundMessageIds.delete(oldest);
      }
    }

    return false;
  }

  /**
   * Send message using signals
   */
  sendMessage(message: any) {
    this._message.set(message);
    // Also push to the Subject stream so subscribers react to each emission once
    this._message$.next(message);
  }
}
